
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { FieldData } from '../types';

interface AddressInputProps {
  onFieldConfirmed: (data: FieldData) => void;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface ParsedAddress {
  city: string;
  state: string;
  zip: string;
}

export default function AddressInput({ onFieldConfirmed }: AddressInputProps) {
  const [inputType, setInputType] = useState<'address' | 'gps'>('address');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapsLoaded, setMapsLoaded] = useState(false);
  
  // Autocomplete state
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load Google Maps API with Places library
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setMapsLoaded(true);
        initializeServices();
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          setMapsLoaded(true);
          initializeServices();
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&libraries=drawing,geometry,places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setMapsLoaded(true);
        initializeServices();
      };
      script.onerror = () => {
        setError('Failed to load Google Maps. Please check your internet connection and refresh the page.');
      };
      document.head.appendChild(script);
    };

    loadGoogleMaps();
  }, []);

  const initializeServices = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      // Create a dummy div for PlacesService (required by API)
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if input looks like GPS coordinates
  const isGPSCoordinates = (input: string): { lat: number; lng: number } | null => {
    // Match patterns like "40.0379, -76.3055" or "40.0379 -76.3055"
    const coordPattern = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/;
    const match = input.trim().match(coordPattern);
    
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
    return null;
  };

  // Fetch address predictions
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      setNoResults(false);
      return;
    }

    // Check if it's GPS coordinates
    const coords = isGPSCoordinates(input);
    if (coords) {
      setPredictions([]);
      setShowDropdown(false);
      setNoResults(false);
      return;
    }

    setIsSearching(true);
    setNoResults(false);

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      (results: PlacePrediction[] | null, status: string) => {
        setIsSearching(false);
        
        if (status === 'OK' && results && results.length > 0) {
          setPredictions(results.slice(0, 5));
          setShowDropdown(true);
          setNoResults(false);
        } else if (status === 'ZERO_RESULTS') {
          setPredictions([]);
          setShowDropdown(true);
          setNoResults(true);
        } else {
          setPredictions([]);
          setShowDropdown(false);
          setNoResults(false);
        }
      }
    );
  }, []);

  // Handle input change with debounce
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    setHighlightedIndex(-1);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.length >= 3) {
      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(value);
      }, 300);
    } else {
      setPredictions([]);
      setShowDropdown(false);
      setNoResults(false);
    }
  };

  // Parse address components from place details
  const parseAddressComponents = (components: any[]): ParsedAddress => {
    const result: ParsedAddress = { city: '', state: '', zip: '' };
    
    components.forEach((component: any) => {
      const types = component.types;
      if (types.includes('locality')) {
        result.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        result.state = component.short_name;
      } else if (types.includes('postal_code')) {
        result.zip = component.long_name;
      }
    });
    
    return result;
  };

  // Handle place selection
  const handleSelectPlace = (prediction: PlacePrediction) => {
    setAddress(prediction.description);
    setShowDropdown(false);
    setPredictions([]);
    setHighlightedIndex(-1);

    if (!placesServiceRef.current) return;

    setIsLoading(true);
    setError('');

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry', 'formatted_address', 'address_components'],
      },
      (place: any, status: string) => {
        if (status === 'OK' && place && place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const parsedAddress = parseAddressComponents(place.address_components || []);

          const mockPolygon = generateMockPolygon(lat, lng);
          const calculatedAcreage = calculateAcreageFromPolygon(mockPolygon);

          const fieldData: FieldData = {
            address: place.formatted_address || prediction.description,
            latitude: lat,
            longitude: lng,
            acreage: calculatedAcreage,
            polygon: mockPolygon,
            boundarySource: 'manual',
            suggestedPolygon: mockPolygon,
            suggestedAcreage: calculatedAcreage,
            city: parsedAddress.city,
            state: parsedAddress.state,
            zip: parsedAddress.zip,
          };

          onFieldConfirmed(fieldData);
        } else {
          setError('Unable to get location details. Please try again.');
        }
        setIsLoading(false);
      }
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleManualSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelectPlace(predictions[highlightedIndex]);
        } else {
          handleManualSubmit();
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle manual form submission (for GPS or direct address entry)
  const handleManualSubmit = async () => {
    setError('');
    setShowDropdown(false);

    if (!mapsLoaded || !window.google || !window.google.maps) {
      setError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }

    setIsLoading(true);

    try {
      let lat: number;
      let lng: number;
      let finalAddress: string;

      if (inputType === 'gps') {
        lat = parseFloat(latitude);
        lng = parseFloat(longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          throw new Error('Invalid GPS coordinates');
        }

        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          throw new Error('GPS coordinates out of range');
        }

        finalAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      } else {
        // Check if address input is GPS coordinates
        const coords = isGPSCoordinates(address);
        if (coords) {
          lat = coords.lat;
          lng = coords.lng;
          finalAddress = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        } else {
          // Geocode the supplied address
          const geocodeResult = await geocodeAddress(address);
          lat = geocodeResult.lat;
          lng = geocodeResult.lng;
          finalAddress = address;
        }
      }

      const mockPolygon = generateMockPolygon(lat, lng);
      const calculatedAcreage = calculateAcreageFromPolygon(mockPolygon);

      const fieldData: FieldData = {
        address: finalAddress,
        latitude: lat,
        longitude: lng,
        acreage: calculatedAcreage,
        polygon: mockPolygon,
        boundarySource: 'manual',
        suggestedPolygon: mockPolygon,
        suggestedAcreage: calculatedAcreage,
      };

      onFieldConfirmed(fieldData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to locate field');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleManualSubmit();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
      {/* Input Type Toggle */}
      <div className="flex items-center justify-center gap-2 mb-8">
        <button
          type="button"
          onClick={() => setInputType('address')}
          className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
            inputType === 'address'
              ? 'bg-emerald-800 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Address
        </button>
        <button
          type="button"
          onClick={() => setInputType('gps')}
          className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
            inputType === 'gps'
              ? 'bg-emerald-800 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          GPS Coordinates
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {inputType === 'address' ? (
          <div className="mb-6 relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Address
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={address}
                onChange={handleAddressChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (predictions.length > 0 || noResults) {
                    setShowDropdown(true);
                  }
                }}
                placeholder="Start typing an address..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none pr-10"
                required
                autoComplete="off"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                </div>
              )}
              {!isSearching && address.length >= 3 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <i className="ri-search-line text-gray-400"></i>
                </div>
              )}
            </div>

            {/* Autocomplete Dropdown */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
              >
                {predictions.length > 0 ? (
                  <ul className="py-1">
                    {predictions.map((prediction, index) => (
                      <li
                        key={prediction.place_id}
                        onClick={() => handleSelectPlace(prediction)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`px-4 py-3 cursor-pointer flex items-start gap-3 transition-colors ${
                          index === highlightedIndex
                            ? 'bg-emerald-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-map-pin-line text-emerald-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {prediction.structured_formatting.main_text}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {prediction.structured_formatting.secondary_text}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : noResults ? (
                  <div className="px-4 py-6 text-center">
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-gray-100 rounded-full">
                      <i className="ri-map-pin-line text-gray-400 text-lg"></i>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">No addresses found</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Try a different search or enter GPS coordinates
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">
              Enter the street address or GPS coordinates (e.g., 40.0379, -76.3055)
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="text"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="40.0379"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="text"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-76.3055"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                required
              />
            </div>
            <p className="text-xs text-gray-500 col-span-2">
              Enter decimal degrees (e.g., 40.0379, -76.3055)
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {!mapsLoaded && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin" />
            Loading Google Maps...
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !mapsLoaded}
          className="w-full bg-emerald-800 text-white font-bold py-4 rounded-lg hover:bg-emerald-700 active:bg-emerald-900 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />
              Locating Field...
            </span>
          ) : !mapsLoaded ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />
              Loading Maps...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-map-pin-line" />
              Find My Field
            </span>
          )}
        </button>
      </form>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-blue-600 text-lg mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="space-y-1 text-blue-800">
              <li>• Start typing to see address suggestions</li>
              <li>• Select an address to automatically locate your field</li>
              <li>• You can also enter GPS coordinates directly</li>
              <li>• Review and adjust the field boundary on the map</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

async function geocodeAddress(address: string): Promise<{ lat: number; lng: number }> {
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps not loaded. Please refresh the page.');
  }

  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address }, (results: any, status: any) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng(),
        });
      } else if (status === 'ZERO_RESULTS') {
        reject(new Error('Address not found. Please check the address and try again.'));
      } else if (status === 'OVER_QUERY_LIMIT') {
        reject(new Error('Too many requests. Please try again in a moment.'));
      } else {
        reject(new Error('Unable to locate address. Please try a different format.'));
      }
    });
  });
}

function generateMockPolygon(
  lat: number,
  lng: number
): Array<{ lat: number; lng: number }> {
  const acreage = 150 + Math.random() * 300;
  const sqMeters = acreage * 4046.86;
  const radius = Math.sqrt(sqMeters / Math.PI) / 111320;

  const points = 8;
  const polygon: Array<{ lat: number; lng: number }> = [];

  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const r = radius * (0.8 + Math.random() * 0.4);
    polygon.push({
      lat: lat + r * Math.cos(angle),
      lng: lng + (r * Math.sin(angle)) / Math.cos((lat * Math.PI) / 180),
    });
  }

  return polygon;
}

function calculateAcreageFromPolygon(
  polygon: Array<{ lat: number; lng: number }>
): number {
  let area = 0;
  const n = polygon.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i].lng * polygon[j].lat;
    area -= polygon[j].lng * polygon[i].lat;
  }

  area = Math.abs(area) / 2;

  const sqMeters =
    area *
    111320 *
    111320 *
    Math.cos((polygon[0].lat * Math.PI) / 180);

  const acres = sqMeters / 4046.86;
  return Math.round(acres * 10) / 10;
}
